/**
 * Shown when a signed-in account reaches a page its role does not hold a grant
 * for — a bookmark kept across a role change, or a link passed between
 * colleagues on different roles.
 *
 * Written as an explanation rather than a rejection, and it names the role, so
 * the reader knows whether to ask for access or has simply followed the wrong
 * link.
 */
import { Link } from 'react-router-dom'
import { ROLE_LABELS } from '@accounting/shared'
import { useSession } from '../lib/session'
import { Button } from '../ui/Button'
import { Card, CardBody } from '../ui/Card'
import { ShieldIcon } from '../ui/icons'

export function Forbidden() {
  const { user } = useSession()

  return (
    <div className="mx-auto max-w-xl py-10">
      <Card>
        <CardBody className="text-center">
          <span className="inline-flex rounded-full bg-brand-50 p-3 text-brand-800 ring-1 ring-brand-200 ring-inset">
            <ShieldIcon width={24} height={24} />
          </span>

          <h1 className="mt-4 font-display text-xl font-semibold text-brand-900">
            Not available to your role
          </h1>

          <p className="mt-2 text-sm leading-relaxed text-ink-600">
            {user
              ? `Your account holds the ${ROLE_LABELS[user.role]} role, which does not include this
                 page. The request would be refused by the API as well, so there is nothing to retry.`
              : 'This page requires a role your session does not hold.'}
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Link to="/">
              <Button variant="primary">Back to overview</Button>
            </Link>
            <Link to="/roles">
              <Button>See what each role may do</Button>
            </Link>
          </div>

          <p className="mt-6 text-xs text-ink-600">
            If you need this access, Finance can change your role — it takes effect on your next
            request, without signing in again.
          </p>
        </CardBody>
      </Card>
    </div>
  )
}
